import { useFetchWrapper } from "../hooks/useFetchWrapper";
import {useLocalStorageNew} from "../hooks/useLocalStorageNew";
import {INTEGRATION_TYPES} from "../utils/constants";
import {usePPTWebView} from "../hooks/usePPTWebView";
import {useSynLMSConfiguration} from "../hooks/useSynLMSConfiguration";
import {useCanvasOAuth} from "../hooks/useCanvasOAuth";
import {notifyError} from "../utils/helpers";
import {D2L_LIST_MODULES_ERROR, MOODLE_LIST_SECTIONS_ERROR} from "../utils/toast-message-constants";
import {useConfig} from "../hooks/useConfig";

export const LTIApiCalls = () => {
    const fetchWrapper = useFetchWrapper();
    const config = useConfig();
    const [, getSession] = useLocalStorageNew("session", {});
    const {isPPTPage, parseToken} = usePPTWebView();
    const {getIntegrationType, isAPISyncOptionAvailable} = useSynLMSConfiguration();
    const {ensureLMSAuthorized} = useCanvasOAuth();
    return {
        gradeBookSync,
        createGradeColumn,
        isLMSAuthorized,
        embedActivity,
        listD2LModules,
        createD2LModule,
        listMoodleSections,
        createMoodleSection,
        listLMSQuizzes,
        listLMSQuizQuestions,
    }

    // Function that syncs the score to the LMS
    function gradeBookSync(pollCode, userId, gradePercentage, callback=()=>{}) {
        return fetchWrapper.post({
            url:`${config.api.integration}/lti/gradebook/score`,
            body: {
                pollCode: pollCode,
                userId: userId,
                gradePercentage: gradePercentage
            }
        }).then(result => {
            // console.log(result);
            callback();
            console.log("Gradebook Sync Done!");
            return true;
        }).catch(err => {
            console.log("Error in Gradebook Sync", err);
            return false;
        })
    }

    // Function that create grade column to the LMS
    function createGradeColumn(pollKey, pollCode) {
        let session = "";
        if (isPPTPage()) {
            session = parseToken();
        } else {
            session = getSession();
        }


        let {intType=""} = session;
        if (intType !== INTEGRATION_TYPES.BLACKBOARD && intType !== INTEGRATION_TYPES.CANVAS && intType !== INTEGRATION_TYPES.D2L) {
            return;
        }

        return fetchWrapper.post({
            url:`${config.api.integration}/lti/gradebook/column`,
            body: {
                intType,
                pollKey,
                pollCode,
            }
        }).then(result => {
           if (result.success) {
               console.log("Create grade column successfully.");
           } else {
               console.log("Fail to Create grade column.");
           }
        }).catch(err => {
            console.log("Error in creating grade column.", err);
            return false;
        })
    }

    // Checks LMS OAuth freshness up front, before any DB writes are made for an embed flow -
    // ensureLMSAuthorized redirects the tab to Canvas's authorization page when the token is stale,
    // so DB writes made before this check would be left applied with no corresponding embed.
    function isLMSAuthorized() {
        let intType = getIntegrationType();
        if (![INTEGRATION_TYPES.CANVAS, INTEGRATION_TYPES.BLACKBOARD, INTEGRATION_TYPES.D2L, INTEGRATION_TYPES.MOODLE].includes(intType)) {
            return Promise.resolve(true);
        }

        return ensureLMSAuthorized(intType);
    }

    function embedActivity(pollKey, pollCode) {
        let intType = getIntegrationType();
        if (![INTEGRATION_TYPES.CANVAS, INTEGRATION_TYPES.BLACKBOARD, INTEGRATION_TYPES.D2L, INTEGRATION_TYPES.MOODLE].includes(intType)) {
            return;
        }

        return fetchWrapper.put({
            url:`${config.api.integration}/lti/activity`,
            body: {
                intType,
                pollKey,
                pollCode,
            }
        }).then(result => {
           if (result.success) {
               console.log("Embed Activity successfully.");
           } else {
               console.log("Fail to embed activity.");
           }
           return result.success;
        }).catch(err => {
            console.log("Error in embedding activity.", err);
            return false;
        });
    }


    function createD2LModule(courseId, moduleName) {
        let intType = getIntegrationType();
        if (intType !== INTEGRATION_TYPES.D2L) {
            return;
        }

        return fetchWrapper.post({
            url:`${config.api.integration}/lti/d2l/module`,
            body: {
                courseId,
                moduleName
            }
        }).then(result => {
           if (result.success) {
               return true;
           } else {
               notifyError("The D2L module cannot be created. Please try again or contact YuJa Help Center. ");
               return false;

           }
        }).catch(err => {
            notifyError("The D2L module cannot be created. Please try again or contact YuJa Help Center. ");
            console.log("Error in creating D2L module.", err);
            return false;
        });
    }

    function listD2LModules(courseId) {
        let intType = getIntegrationType();
        let apiSyncAvailable = isAPISyncOptionAvailable();
        if (intType !== INTEGRATION_TYPES.D2L || !apiSyncAvailable) {
            return [];
        }

        return fetchWrapper.get({
            url:`${config.api.integration}/lti/d2l/modules?courseId=${courseId}`,
        }).then(res => {
            if (res.success) {
                return res.result;
            } else {
                console.log("Fail to list D2L modules.");
                return [];
            }
        }).catch(err => {
            notifyError(D2L_LIST_MODULES_ERROR);
            console.log("Error in listing D2L modules.", err);
            return [];
        })
    }


    function listMoodleSections(courseId) {
        let intType = getIntegrationType();
        let apiSyncAvailable = isAPISyncOptionAvailable();
        if (intType !== INTEGRATION_TYPES.MOODLE || !apiSyncAvailable) {
            return [];
        }

        return fetchWrapper.get({
            url:`${config.api.integration}/lti/moodle/sections?courseId=${courseId}`,
        }).then(res => {
            if (res.success) {
                return res.result;
            } else {
                console.log("Fail to list Moodle sections.");
                return [];
            }
        }).catch(err => {
            notifyError(MOODLE_LIST_SECTIONS_ERROR);
            console.log("Error in listing Moodle sections.", err);
            return [];
        })
    }

    function createMoodleSection(courseId, sectionName) {
        let intType = getIntegrationType();
        if (intType !== INTEGRATION_TYPES.MOODLE) {
            return;
        }

        return fetchWrapper.post({
            url:`${config.api.integration}/lti/moodle/section`,
            body: {
                courseId,
                sectionName
            }
        }).then(result => {
            if (result.success) {
                return true;
            } else {
                notifyError("The Moodle section cannot be created. Please try again or contact YuJa Help Center. ");
                return false;

            }
        }).catch(err => {
            notifyError("The Moodle Section cannot be created. Please try again or contact YuJa Help Center. ");
            console.log("Error in creating Moodle Section.", err);
            return false;
        });
    }


    /**
     * list LMS Quizzes, now just for Canvas
     * @param courseId
     * @returns {Promise<array>}
     */
    function listLMSQuizzes(courseId) {
        let intType = getIntegrationType();
        return ensureLMSAuthorized(intType).then(authorized => {
            if (!authorized) {
                return [];
            }

            return fetchWrapper.get({
                url:`${config.api.integration}/lti/quizzes/${intType}/${courseId}`,
            }).then(result => {
                if (result.success) {
                    return result.result;
                } else {
                    console.log("Error in loading quizzes from LMS.");
                    return [];

                }
            }).catch(err => {
                notifyError("Cannot load quizzes. Please try again or contact YuJa Help Center. ");
                console.log("Error in listing quizzes from LMS.", err);
                return [];
            });
        });
    }

    /**
     * list LMS Quiz Questions, now just for Canvas
     * @param courseId
     * @param quizId
     * @param quizType
     *
     * @returns {Promise<array>}
     */
    function listLMSQuizQuestions(courseId, quizId, quizType="") {
        let intType = getIntegrationType();
        let url = `${config.api.integration}/lti/quiz/questions/${intType}/${courseId}/${quizId}`;
        if (!!quizType) {
            url = url + "?quizType=" + quizType;
        }
        return ensureLMSAuthorized(intType).then(authorized => {
            if (!authorized) {
                return [];
            }

            return fetchWrapper.get({
                url,
            }).then(result => {
                if (result.success) {
                    return result.result;
                } else {
                    console.log("Error in loading quiz questions from LMS.");
                    return [];

                }
            }).catch(err => {
                notifyError("Cannot load quiz questions. Please try again or contact YuJa Help Center. ");
                console.log("Error in listing quizzes from LMS.", err);
                return [];
            });
        });
    }
}
